import { useState, useEffect } from 'react';
import { useUIState } from '../../contexts/UIStateContext';
import MarkdownEditor from '../shared/MarkdownEditor';
import CollapsibleCard from '../shared/CollapsibleCard';
import SystemCustomDualList from '../shared/SystemCustomDualList';
import Dropdown from '../shared/Dropdown';
import EditSaveButtons from '../shared/EditSaveButtons';
import { adminApi } from '../../lib/admin-api';

export default function FunctionsTab({ configuration, configId, type, onUpdate, onRefreshConfiguration }) {
  const { getAdminSubTab, setAdminSubTab, getAdminSelectedItem, setAdminSelectedItem } = useUIState();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('configuration');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update sub-tab when configuration changes (restore saved state)
  useEffect(() => {
    if (configId) {
      const savedSubTab = getAdminSubTab(configId);
      setActiveSubTab(savedSubTab);
    }
  }, [configId]);

  // Wrapper to persist sub-tab to UIState (per config)
  const handleSetActiveSubTab = (subTab) => {
    setActiveSubTab(subTab);
    setAdminSubTab(configId, subTab);
  };

  // Wrapper to persist selected item to UIState (per config)
  const handleSetSelectedItem = (item) => {
    setSelectedItem(item);
    setAdminSelectedItem(configId, item?.id || null);
  };
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form state for selected item
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [parametersSchema, setParametersSchema] = useState('{}');
  const [executionTimeout, setExecutionTimeout] = useState(30);
  const [rateLimit, setRateLimit] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [category, setCategory] = useState('');

  // Arguments state
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [systemArguments, setSystemArguments] = useState([]);
  const [customArguments, setCustomArguments] = useState([]);

  // Conditions state
  const [conditions, setConditions] = useState({});
  const [selectedConditionCode, setSelectedConditionCode] = useState(null);
  const [conditionsPreview, setConditionsPreview] = useState({});
  const [systemConditions, setSystemConditions] = useState([]);
  const [systemConditionsPreview, setSystemConditionsPreview] = useState([]);
  const [isLoadingConditionsPreview, setIsLoadingConditionsPreview] = useState(false);

  // Task parameters for preview (non-autonomous only)
  const [taskParameters, setTaskParameters] = useState([]);

  // Load functions from configuration
  useEffect(() => {
    if (configuration && configId) {
      const data = configuration.functions;
      setItems(data || []);
      setLoading(false);

      // Try to restore previously selected item for this config
      const persistedItemId = getAdminSelectedItem(configId);
      let itemToSelect = null;

      if (persistedItemId && data && data.length > 0) {
        // Try to find the persisted item
        itemToSelect = data.find(item => item.id === persistedItemId);
      }

      // If no persisted item found, select first item
      if (!itemToSelect && data && data.length > 0) {
        itemToSelect = data[0];
      }

      if (itemToSelect) {
        setSelectedItem(itemToSelect);
        setAdminSelectedItem(configId, itemToSelect.id);
      }
    }
  }, [configuration, configId]);

  // Update form when selected item changes
  useEffect(() => {
    if (selectedItem) {
      setDescription(selectedItem.description || '');
      setPrompt(selectedItem.function_prompt || selectedItem.prompt || '');
      setExecutionTimeout(selectedItem.timeout || selectedItem.execution_timeout || 30);
      setRateLimit(selectedItem.rate_limit || null);
      setEnabled(selectedItem.enabled !== false);
      setCategory(selectedItem.category || '');

      // Parse parameters schema
      if (selectedItem.parameters_schema) {
        try {
          const schema = selectedItem.parameters_schema;
          const props = schema.properties || {};
          const required = schema.required || [];

          // Convert to array of parameters
          const paramArray = Object.entries(props).map(([name, config]) => ({
            name,
            type: config.type || 'string',
            description: config.description || '',
            required: required.includes(name)
          }));

          setParameters(paramArray);
          setParametersSchema(JSON.stringify(schema, null, 2));

          // Auto-select first parameter if available
          if (paramArray.length > 0) {
            setSelectedParameter(paramArray[0]);
          } else {
            setSelectedParameter(null);
          }
        } catch (error) {
          console.error('Failed to parse parameters schema:', error);
          setParameters([]);
          setParametersSchema('{}');
          setSelectedParameter(null);
        }
      } else {
        setParameters([]);
        setParametersSchema('{}');
        setSelectedParameter(null);
      }

      // Load conditions
      if (selectedItem.conditions) {
        setConditions(selectedItem.conditions);
        // Auto-select first condition if available
        const conditionCodes = Object.keys(selectedItem.conditions);
        if (conditionCodes.length > 0) {
          setSelectedConditionCode(conditionCodes[0]);
        } else {
          setSelectedConditionCode(null);
        }
      } else {
        setConditions({});
        setSelectedConditionCode(null);
      }

      setIsEditMode(false); // Exit edit mode when switching items
    }
  }, [selectedItem]);

  // Load arguments preview when not in edit mode and on arguments tab
  useEffect(() => {
    if (!isEditMode && activeSubTab === 'arguments' && selectedItem && configId) {
      loadArgumentsPreview();
    }
  }, [isEditMode, activeSubTab, selectedItem, configId]);

  // Load conditions preview when not in edit mode and on conditions tab
  useEffect(() => {
    if (!isEditMode && activeSubTab === 'conditions' && selectedItem && configId) {
      loadConditionsPreview();
    }
  }, [isEditMode, activeSubTab, selectedItem, configId]);

  const loadArgumentsPreview = async () => {
    if (!configId || !selectedItem) return;

    try {
      const preview = await adminApi.modelConfigurations.previewPrompts(configId);

      // Find this function's preview in the response
      const functionPreview = preview.function_prompts.find(fp => fp.id === selectedItem.id);
      if (functionPreview) {
        setSystemArguments(functionPreview.system_arguments || []);
        setCustomArguments(functionPreview.custom_arguments || []);
      } else {
        setSystemArguments([]);
        setCustomArguments([]);
      }
    } catch (error) {
      console.error('Failed to load arguments preview:', error);
      setSystemArguments([]);
      setCustomArguments([]);
    }
  };

  const loadConditionsPreview = async () => {
    if (!configId || !selectedItem) return;

    setIsLoadingConditionsPreview(true);
    try {
      const preview = await adminApi.modelConfigurations.previewPrompts(configId);

      // Set system conditions (global, same for all functions)
      if (preview.system_conditions) {
        setSystemConditionsPreview(preview.system_conditions);
      } else {
        setSystemConditionsPreview([]);
      }

      // Find this function's preview in the response
      const functionPreview = preview.function_prompts.find(fp => fp.id === selectedItem.id);
      if (functionPreview && functionPreview.conditions) {
        setConditionsPreview(functionPreview.conditions);
      } else {
        setConditionsPreview({});
      }
    } catch (error) {
      console.error('Failed to load conditions preview:', error);
      setConditionsPreview({});
      setSystemConditionsPreview([]);
    } finally {
      setIsLoadingConditionsPreview(false);
    }
  };

  const handleCancel = () => {
    // Reset all form values to current selected item
    if (selectedItem) {
      setDescription(selectedItem.description || '');
      setPrompt(selectedItem.function_prompt || selectedItem.prompt || '');
      setExecutionTimeout(selectedItem.timeout || selectedItem.execution_timeout || 30);
      setRateLimit(selectedItem.rate_limit || null);
      setEnabled(selectedItem.enabled !== false);
      setCategory(selectedItem.category || '');

      // Reset parameters from original schema
      if (selectedItem.parameters_schema) {
        try {
          const schema = selectedItem.parameters_schema;
          const props = schema.properties || {};
          const required = schema.required || [];

          const paramArray = Object.entries(props).map(([name, config]) => ({
            name,
            type: config.type || 'string',
            description: config.description || '',
            required: required.includes(name)
          }));

          setParameters(paramArray);
          if (paramArray.length > 0 && selectedParameter) {
            // Find the current selected parameter in the reset array
            const resetParam = paramArray.find(p => p.name === selectedParameter.name);
            if (resetParam) {
              setSelectedParameter(resetParam);
            }
          }
        } catch (error) {
          console.error('Failed to reset parameters:', error);
        }
      }
    }
    setIsEditMode(false);
  };

  const handleSave = async () => {
    if (!selectedItem) return;

    setIsSaving(true);
    try {
      const updates = {
        description,
        prompt,
        execution_timeout: executionTimeout,
        rate_limit: rateLimit,
        enabled,
        category
      };

      // If on Arguments tab, save custom_arguments (description overrides)
      if (activeSubTab === 'arguments') {
        const customArgs = {};
        customArguments.forEach(arg => {
          customArgs[arg.name] = {
            description: arg.description
          };
        });
        updates.custom_arguments = Object.keys(customArgs).length > 0 ? customArgs : null;
      }

      // If on Conditions tab, save conditions
      if (activeSubTab === 'conditions') {
        updates.conditions = conditions;
      }

      // Call function configuration API
      await adminApi.functionConfigurations.update(selectedItem.id, updates);

      // Refresh configuration from server to get latest data
      await onRefreshConfiguration?.();

      setIsEditMode(false); // Exit edit mode after successful save
      alert('Function configuration saved successfully');
    } catch (error) {
      console.error('Failed to save function configuration:', error);
      alert('Failed to save function configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const subTabs = [
    { id: 'configuration', label: 'Configuration', shortLabel: 'Config', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
    { id: 'prompt', label: 'Instructions', shortLabel: 'Inst', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { id: 'arguments', label: 'Arguments', shortLabel: 'Arg', icon: 'M4 6h16M4 12h16M4 18h16' },
    { id: 'conditions', label: 'Conditions', shortLabel: 'Cond', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' }
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Detail View */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedItem ? (
          <>
            {/* Header with Dropdown and Sub-tabs */}
            <div className="border-b px-6 pt-4 flex-shrink-0" style={{ borderColor: 'var(--color-border-default)', backgroundColor: 'var(--color-bg-primary)' }}>
              <div className="flex items-center gap-4 mb-3">
                <label className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
                  Function:
                </label>
                <div className="flex-1 max-w-md">
                  <Dropdown
                    value={selectedItem.id}
                    onChange={(value) => {
                      const item = items.find(i => i.id === value);
                      if (item) handleSetSelectedItem(item);
                    }}
                    options={[...items]
                      .sort((a, b) => {
                        // Sort functions by category
                        const categoryA = (a.category || 'zzz').toLowerCase();
                        const categoryB = (b.category || 'zzz').toLowerCase();
                        return categoryA.localeCompare(categoryB);
                      })
                      .map((item) => {
                        const itemName = item.function_name || item.task_name || item.name;
                        const maxLength = 50; // Maximum characters before truncation
                        const truncatedName = itemName.length > maxLength
                          ? itemName.substring(0, maxLength) + '...'
                          : itemName;
                        return {
                          value: item.id,
                          label: `${item.enabled ? '✓' : '○'} ${truncatedName}`
                        };
                      })}
                  />
                </div>
              </div>
              <div className="flex gap-1">
                {subTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleSetActiveSubTab(tab.id)}
                    className={`flex items-center ${windowWidth > 650 ? 'gap-2' : ''} px-4 py-2 rounded-t-lg transition-all duration-200 ${
                      activeSubTab === tab.id
                        ? 'border-t border-x'
                        : ''
                    }`}
                    style={
                      activeSubTab === tab.id
                        ? {
                            backgroundColor: 'var(--color-bg-primary)',
                            borderColor: 'var(--color-border-default)',
                            color: 'var(--color-text-primary)'
                          }
                        : {
                            color: 'var(--color-text-muted)'
                          }
                    }
                    onMouseEnter={(e) => {
                      if (activeSubTab !== tab.id) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSubTab !== tab.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--color-text-muted)';
                      }
                    }}
                  >
                    {windowWidth > 650 && (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                      </svg>
                    )}
                    <span className="font-medium text-sm">{windowWidth > 650 ? tab.label : (tab.shortLabel || tab.label)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto px-8 pt-4 pb-6" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
              {activeSubTab === 'configuration' && (
                <div key={`config-${selectedItem.id}`} className="max-w-3xl space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        {selectedItem.function_name || selectedItem.task_name || selectedItem.name}
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        Configure behavior and limits for this function
                      </p>
                    </div>

                    {/* Edit/Save/Cancel Buttons */}
                    <EditSaveButtons
                      isEditMode={isEditMode}
                      isSaving={isSaving}
                      onEdit={() => setIsEditMode(true)}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  </div>

                  {/* Enabled Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-300">
                        Enable Function
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {enabled ? `This function is active and can be used` : `This function is disabled and will not be used`}
                      </p>
                    </div>
                    <button
                      onClick={() => setEnabled(!enabled)}
                      disabled={!isEditMode}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                        enabled ? 'bg-purple-600' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Description
                      <span className="text-gray-500 ml-2 font-normal">Brief description shown in system prompt</span>
                    </label>
                    <div className="h-[200px]">
                      <MarkdownEditor
                        content={description}
                        onSave={setDescription}
                        externalControls={true}
                        isEditing={isEditMode}
                        textareaClassName="h-full"
                        placeholder={`Describe what this function does...`}
                        showBorder={true}
                      />
                    </div>
                  </div>

                  {/* Category, Timeout, and Rate Limit Row */}
                  <div className="grid gap-6 grid-cols-3">
                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Category
                      </label>
                      <input
                        type="text"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="e.g., core, search"
                        disabled={!isEditMode}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                      />
                    </div>

                    {/* Execution Timeout */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Timeout (seconds)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={executionTimeout}
                        onChange={(e) => setExecutionTimeout(parseInt(e.target.value) || 30)}
                        disabled={!isEditMode}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                      />
                    </div>

                    {/* Rate Limit */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Rate Limit
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={rateLimit || ''}
                        onChange={(e) => setRateLimit(e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="Unlimited"
                        disabled={!isEditMode}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeSubTab === 'prompt' && (
                <div key={`prompt-${selectedItem.id}`} className="max-w-5xl h-full flex flex-col">
                  <div className="flex items-start justify-between mb-6 flex-shrink-0">
                    <div>
                      <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        Instructions
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        Detailed instructions for how the AI should use this function
                      </p>
                    </div>

                    {/* Edit/Save/Cancel Buttons */}
                    <EditSaveButtons
                      isEditMode={isEditMode}
                      isSaving={isSaving}
                      onEdit={() => setIsEditMode(true)}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  </div>

                  <div className="flex-1 flex flex-col min-h-0">
                    <label className="block text-sm font-medium text-gray-300 mb-2 flex-shrink-0">
                      Prompt Template
                      <span className="text-gray-500 ml-2 font-normal">Instructions given to the AI</span>
                    </label>
                    <div className="flex-1 min-h-0">
                      <MarkdownEditor
                        content={prompt}
                        onSave={setPrompt}
                        externalControls={true}
                        isEditing={isEditMode}
                        textareaClassName="h-full"
                        placeholder={`Enter detailed instructions for using this function...`}
                        showBorder={true}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeSubTab === 'arguments' && (
                <div key={`arguments-${selectedItem.id}`} className="max-w-5xl space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        Function Arguments
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        Define the parameters this function accepts when called by the AI
                      </p>
                    </div>

                    {/* Edit/Save/Cancel Buttons */}
                    <EditSaveButtons
                      isEditMode={isEditMode}
                      isSaving={isSaving}
                      onEdit={() => setIsEditMode(true)}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  </div>

                  {/* Arguments List */}
                  {(systemArguments.length + customArguments.length) === 0 ? (
                    <div className="text-center py-12 rounded-lg" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
                      <svg className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-disabled)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm">No arguments configured for this function</p>
                    </div>
                  ) : (
                    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
                      <SystemCustomDualList
                        customItems={customArguments.map(arg => ({ id: arg.name, ...arg }))}
                        systemItems={systemArguments.map(arg => ({ id: arg.name, ...arg }))}
                        itemTypeSingular="Argument"
                        itemTypePlural="Arguments"
                        customSectionDescription="Function-specific arguments you can customize"
                        systemSectionDescription="Function signature arguments (read-only)"
                        onCreateCustom={null}
                        onDeleteCustom={null}
                        renderCustomItemHeader={(item) => (
                          <>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium truncate">{item.name}</h5>
                              <p className="text-xs text-gray-500 truncate">{item.type}</p>
                            </div>
                            {item.required && (
                              <span className="px-2 py-1 bg-green-900/30 text-green-400 border border-green-500/30 rounded text-xs font-medium">
                                Required
                              </span>
                            )}
                          </>
                        )}
                        renderCustomItemExpanded={(item) => {
                          const paramIndex = customArguments.findIndex(p => p.name === item.name);
                          return (
                            <div className="border border-gray-700 rounded-lg p-4 space-y-3">
                              {/* Description */}
                              <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                                  Description
                                </label>
                                <div className="h-[150px]">
                                  <MarkdownEditor
                                    content={item.description || ''}
                                    onSave={(newDescription) => {
                                      const newCustomArgs = [...customArguments];
                                      newCustomArgs[paramIndex] = { ...newCustomArgs[paramIndex], description: newDescription };
                                      setCustomArguments(newCustomArgs);
                                    }}
                                    externalControls={true}
                                    isEditing={isEditMode}
                                    textareaClassName="h-full"
                                    placeholder="Describe what this argument does..."
                                    showBorder={true}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }}
                        renderSystemItemContent={(item) => (
                          <>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium truncate">{item.name}</h5>
                              <p className="text-sm text-gray-400 truncate">{item.description}</p>
                              <p className="text-xs text-gray-500 mt-1">{item.type}</p>
                            </div>
                            {item.required && (
                              <span className="px-2 py-1 bg-amber-900/30 text-amber-400 border border-amber-500/30 rounded text-xs font-medium">
                                Required
                              </span>
                            )}
                          </>
                        )}
                        initialCustomExpanded={customArguments.length > 0}
                        initialSystemExpanded={false}
                        isLoading={false}
                        customEmptyMessage="No custom arguments for this function"
                        systemEmptyMessage="No system arguments"
                      />
                    </div>
                  )}
                </div>
              )}

              {activeSubTab === 'conditions' && (
                <div key={`conditions-${selectedItem.id}`} className="max-w-5xl space-y-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                        Function Conditions
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {isEditMode
                          ? 'Define condition-specific guidance using templates with {{parameter}} placeholders'
                          : 'Preview with actual parameter values from database'}
                      </p>
                    </div>

                    {/* Edit/Save/Cancel Buttons */}
                    <EditSaveButtons
                      isEditMode={isEditMode}
                      isSaving={isSaving}
                      onEdit={() => setIsEditMode(true)}
                      onSave={handleSave}
                      onCancel={handleCancel}
                    />
                  </div>

                  {/* Conditions List */}
                  {Object.keys(conditions).length === 0 && systemConditionsPreview.length === 0 ? (
                    <div className="text-center py-12 rounded-lg" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
                      <svg className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-disabled)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-sm">No conditions configured for this function</p>
                    </div>
                  ) : (
                    <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-default)', borderWidth: '1px' }}>
                      {isLoadingConditionsPreview && !isEditMode && (
                        <div className="bg-gray-900/50 backdrop-blur-sm p-4 flex items-center justify-center">
                          <div className="flex items-center gap-2 text-purple-400">
                            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-sm font-medium">Loading preview...</span>
                          </div>
                        </div>
                      )}
                      <SystemCustomDualList
                        customItems={Object.entries(isEditMode ? conditions : (conditionsPreview || conditions)).map(([code, cond]) => ({
                          id: code,
                          code: code,
                          ...cond
                        }))}
                        systemItems={isEditMode ? [] : systemConditionsPreview.map(cond => ({
                          id: cond.code,
                          code: cond.code,
                          name: cond.code,
                          ...cond
                        }))}
                        itemTypeSingular="Condition"
                        itemTypePlural="Conditions"
                        customSectionDescription="Function-specific conditions you can edit"
                        systemSectionDescription="Global conditions checked at runtime (read-only)"
                        onCreateCustom={null}
                        onDeleteCustom={null}
                        renderCustomItemHeader={(item) => (
                          <>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium truncate">{item.code}</h5>
                              {item.description && (
                                <p className="text-sm text-gray-400 truncate">{item.description}</p>
                              )}
                            </div>
                            {item.label && (
                              <span className="px-2 py-1 bg-yellow-900/30 text-yellow-400 border border-yellow-500/30 rounded text-xs font-medium">
                                {item.label}
                              </span>
                            )}
                          </>
                        )}
                        renderCustomItemExpanded={(item) => (
                          <div className="border border-gray-700 rounded-lg p-4 space-y-3">
                            {/* Label */}
                            <div>
                              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                                Label
                              </label>
                              <input
                                type="text"
                                value={item.label || ''}
                                disabled={!isEditMode}
                                onChange={(e) => {
                                  const newConditions = { ...conditions };
                                  newConditions[item.code] = { ...newConditions[item.code], label: e.target.value };
                                  setConditions(newConditions);
                                }}
                                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                placeholder="Brief label for this condition"
                              />
                            </div>

                            {/* Description */}
                            <div>
                              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                                Description
                              </label>
                              <input
                                type="text"
                                value={item.description || ''}
                                disabled={!isEditMode}
                                onChange={(e) => {
                                  const newConditions = { ...conditions };
                                  newConditions[item.code] = { ...newConditions[item.code], description: e.target.value };
                                  setConditions(newConditions);
                                }}
                                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                                placeholder="When this condition occurs..."
                              />
                            </div>

                            {/* Guidance */}
                            <div>
                              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                                AI Guidance
                              </label>
                              <div className="h-[150px]">
                                <MarkdownEditor
                                  content={item.guidance || ''}
                                  onSave={(newGuidance) => {
                                    const newConditions = { ...conditions };
                                    newConditions[item.code] = { ...newConditions[item.code], guidance: newGuidance };
                                    setConditions(newConditions);
                                  }}
                                  externalControls={true}
                                  isEditing={isEditMode}
                                  textareaClassName="h-full"
                                  placeholder="Instructions for the AI when this condition occurs..."
                                  showBorder={true}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        renderSystemItemContent={(item) => (
                          <>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium truncate">{item.code}</h5>
                              {item.description && (
                                <p className="text-sm text-gray-400 truncate">{item.description}</p>
                              )}
                            </div>
                            <span className="px-2 py-1 bg-amber-900/30 text-amber-400 border border-amber-500/30 rounded text-xs font-medium">
                              {item.label}
                            </span>
                          </>
                        )}
                        initialCustomExpanded={Object.keys(conditions).length > 0}
                        initialSystemExpanded={false}
                        isLoading={false}
                        customEmptyMessage="No custom conditions configured for this function"
                        systemEmptyMessage="No system conditions available"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="h-full flex items-center justify-center" style={{ color: 'var(--color-text-muted)' }}>
            <div className="text-center">
              <svg className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-disabled)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <p className="text-lg">Select a function to view details</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
